import { PublicClientApplication } from "@azure/msal-browser";
import { config } from "src/config";
import { SSOLoginApi } from "src/api/api";
import LogRocket from "logrocket";
import { isMobileDevice, setViewPreference } from "src/utils/deviceDetection";

export function loginConfig() {
  return new PublicClientApplication({
    auth: {
      clientId: config.appId,
      redirectUri: config.redirectUri,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: true,
    },
  });
}

// SSO failures are surfaced by the login page's CAlert banner, never window.alert.
// The flag lets Login.js show these messages as-is and stops the popup-fallback
// logic from treating a server rejection as a blocked popup.
const ssoError = (message) => Object.assign(new Error(message), { isSsoError: true });

// Check if crypto.subtle is available (required for PKCE)
function isCryptoAvailable() {
  return typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.subtle &&
    typeof window.crypto.subtle.digest === 'function';
}

export async function loginWithMicrosoft() {
  try {

    // Check if crypto API is available (required for MSAL PKCE)
    if (!isCryptoAvailable()) {
      const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isSecureContext) {
        throw new Error("Microsoft login requires HTTPS. Please access the app via HTTPS or use localhost for testing. Current URL: " + window.location.href);
      } else {
        throw new Error("Web Crypto API is not available in this browser. Please try a different browser or update your browser.");
      }
    }

    // Validate config
    if (!config.appId) {
      throw new Error("Microsoft SSO App ID is not configured. Please check your environment variables.");
    }
    if (!config.redirectUri) {
      throw new Error("Microsoft SSO Redirect URI is not configured. Please check your environment variables.");
    }

    const publicClientApplication = loginConfig();
    const isMobile = isMobileDevice();

    if (isMobile) {
      // Use redirect flow for mobile devices (popups don't work well on mobile)
      console.log("Using redirect flow for mobile");
      try {
        await publicClientApplication.loginRedirect({
          scopes: config.scopes,
          prompt: "select_account",
        });
        // The redirect will navigate away, so we don't need to handle the result here
        // The result will be handled when the user returns via handleRedirectPromise
        return;
      } catch (redirectErr) {
        throw redirectErr;
      }
    } else {
      // Use popup flow for desktop. Only the Microsoft popup itself gets the
      // redirect fallback  a server rejection after a successful popup must
      // surface to the login page, not bounce the user back to Microsoft.
      try {
        await publicClientApplication.loginPopup({
          scopes: config.scopes,
          prompt: "select_account",
        });
      } catch (popupErr) {
        await publicClientApplication.loginRedirect({
          scopes: config.scopes,
          prompt: "select_account",
        });
        return;
      }

      const user = publicClientApplication.getAllAccounts();
      if (user.length > 0) {
        await processSSOLogin(publicClientApplication, user);
      } else {
        await loginWithMicrosoft();
      }
    }
  } catch (err) {
    if (err.isSsoError) throw err;
    // If popup is blocked or fails, try redirect as fallback
    if (err.errorCode === "popup_window_error" || err.name === "BrowserAuthError" || err.message?.includes("popup")) {
      try {
        const publicClientApplication = loginConfig();
        await publicClientApplication.loginRedirect({
          scopes: config.scopes,
          prompt: "select_account",
        });
      } catch (redirectErr) {
        throw redirectErr;
      }
    } else {
      throw err;
    }
  }
}

export async function handleRedirectPromise() {
  try {
    const publicClientApplication = loginConfig();

    // Log for debugging mobile issues
    const hasHashFragment = window.location.hash && (
      window.location.hash.includes('code=') ||
      window.location.hash.includes('error=')
    );
    if (hasHashFragment) {
      console.log("Processing OAuth callback with hash fragment:", window.location.hash.substring(0, 100) + "...");
    }

    const response = await publicClientApplication.handleRedirectPromise();

    if (response !== null) {
      // User just returned from redirect
      console.log("OAuth response received, processing login...");
      const user = publicClientApplication.getAllAccounts();
      if (user.length > 0) {
        await processSSOLogin(publicClientApplication, user);
      } else {
        console.warn("No user accounts found after OAuth redirect");
      }
    } else if (hasHashFragment) {
      // If we have a hash fragment but no response, there might be an error
      console.warn("Hash fragment detected but no response from MSAL. Hash:", window.location.hash);
    }

    return response;
  } catch (err) {
    console.error("Error in handleRedirectPromise:", err);
    // Redirect flow has no caller UI  the app is booting. Stash the message so
    // the login page shows it in its banner on mount.
    if (err.isSsoError) {
      sessionStorage.setItem('ssoLoginError', err.message);
    }
    // Don't silently fail - log the error for debugging
    if (err.errorCode) {
      console.error("MSAL Error Code:", err.errorCode);
      console.error("MSAL Error Message:", err.errorMessage || err.message);
    }
    return null;
  }
}

async function processSSOLogin(publicClientApplication, user) {
  // Acquire the Microsoft ID token for the signed-in account. The server verifies
  // its signature locally before issuing a session token, so a token  not a bare
  // email  is what proves the Microsoft login actually happened.
  let idToken;
  try {
    const tokenResponse = await publicClientApplication.acquireTokenSilent({
      scopes: config.scopes,
      account: user[0],
    });
    idToken = tokenResponse.idToken;
  } catch (tokenErr) {
    console.error("Failed to acquire Microsoft access token:", tokenErr);
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    throw ssoError("Login failed. Please try again.");
  }

  const userData = { idToken };

  try {
    const res = await SSOLoginApi(userData);
    if (res.data.message === "success") {
      // making logs
      LogRocket.identify(res.data.id, {
        name: res.data.email,
        email: res.data.email,
      });
      localStorage.setItem("healthcareToken", JSON.stringify(res.data.healthcare.data));
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("role", res.data.role);
      localStorage.setItem("userEmail", res.data.email);
      localStorage.setItem("userData", JSON.stringify(user));

      // Clear hash fragment before redirect to prevent issues
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      // Check if mobile device and redirect accordingly
      if (isMobileDevice()) {
        setViewPreference('mobile');
        localStorage.setItem('loggedInWithMobile', 'true');
        // Use full URL to ensure proper navigation on mobile
        window.location.href = window.location.origin + '/mobile/referral';
      } else {
        localStorage.setItem('loggedInWithMobile', 'false');
        window.location.reload();
      }
    } else if (res.data.message === "invalid") {
      // Clear hash fragment on error
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      throw ssoError("User doesn't exist. Please contact your administrator.");
    }
  } catch (err) {
    if (err.isSsoError) throw err;
    console.error("SSO login API error:", err);
    // Clear hash fragment on error
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    throw ssoError("Login failed. Please try again.");
  }
}
